// DirectQuoteForm.jsx
import { useState, useEffect, useContext } from 'react';
import { Save, ArrowLeft, FileDown, Plus, Printer } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import BasicInfoSection from './components/BasicInfoSection';
import PortDetailsSection from './components/PortDetailsSection';
import EquipmentSection from './components/EquipmentSection';
import CarrierOptionSection from './components/CarrierOptionSection';
import TermsConditionsSection from './components/TermsConditionsSection';
import { generateQuoteNumber } from '../../utils/quoteUtils';
import { fetchQuoteById, updateQuote, createNewQuote } from '../../api/QuoteApi';
import { generateDirectQuotePDF, printDirectQuotePDF } from './utils/pdfGenerator';
import { fetchAccountAddress } from '../../api/AccountApi';
import { AuthContext } from "../../context/AuthContext";
import { fetchUserDetailsByRoleID } from '../../api/UserRoleApi';
import { toast } from '../../components/Toast';

export default function DirectQuoteForm({ category, mode }) {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const quoteId = searchParams.get('id');
  const isEditMode = searchParams.get('edit') === 'true';
  const isViewMode = quoteId && !isEditMode;
  const [userDetails, setUserDetails] = useState(null);

  const [loading, setLoading] = useState(false);

  // Define disabled state early to avoid "Cannot access before initialization" error
  const disabled = isViewMode;

  useEffect(() => {
      if (user) {
        console.log("Current user in header:", user);
        getUserById(user.id);
      }
    }, [user]);
    
    const getUserById = async (userId) => {
      try {
        const res = await fetchUserDetailsByRoleID(userId);
        console.log("Fetched user data:", res);
        // API returns an array, so take the first item
        if (res && res.length > 0) {
          setUserDetails(res[0]);
        }
      } catch (error) {
        console.error("Error fetching user details:", error);
      }
    };
  
  // Create default carrier option structure
  const createDefaultOption = () => ({
    carrier: '',
    incoterm: '',
    currency: '',
    cargoType: '',
    // New structure: freightChargesTables (array of tables)
    freightChargesTables: [{
      tableName: 'Default',
      charges: [{
        carrier: '',
        unitType: '',
        numberOfUnits: '',
        amount: '',
        currency: '',
        transitTime: '',
        numberOfRouting: '',
        surcharge: '',
        frequency: '',
        total: 0,
        remarks: ''
      }]
    }],
    // Keep old format for backward compatibility during migration
    freightCharges: [{
      carrier: '',
      unitType: '',
      numberOfUnits: '',
      amount: '',
      currency: '',
      transitTime: '',
      numberOfRouting: '',
      surcharge: '',
      frequency: '',
      total: 0,
      remarks: ''
    }],
    seaFreightRatioCharges: [],
    destinationCharges: [{
      chargeName: '',
      unitType: '',
      numberOfUnits: '',
      amount: '',
      currency: '',
      total: 0,
      remark: ''
    }],
    originHandling: [{
      chargeName: '',
      unitType: '',
      numberOfUnits: '',
      amount: '',
      currency: '',
      total: 0,
      remark: ''
    }],
    destinationHandling: [{
      chargeName: '',
      unitType: '',
      numberOfUnits: '',
      amount: '',
      currency: '',
      total: 0,
      remark: ''
    }]
  });

  const [formData, setFormData] = useState({
    quoteNumber: '',
    createdDate: new Date().toISOString().split('T')[0],
    rateValidity: '',
    customer: '',
    pickupLocation: '',
    deliveryLocation: '',
    portOfLoading: '',
    portOfDischarge: '',
    equipment: { equipment: '', units: '', netWeight: '', grossWeight: '', volume: '', chargeableWeight: '' },
    carrierOptions: [createDefaultOption()], // Array of carrier options
    termsConditions: []
  });

  useEffect(() => {
    if (quoteId) {
      loadQuoteData();
    } else {
      // Generate quote number only for new quotes
      setFormData(prev => ({
        ...prev,
        quoteNumber: generateQuoteNumber()
      }));
    }
  }, [quoteId]);

  const formatDate = (dateString) => {
    if (!dateString) return "";
    return dateString.split("T")[0];
  };

  const loadQuoteData = async () => {
    try {
      setLoading(true);
      const data = await fetchQuoteById(quoteId);
      console.log("=== LOAD QUOTE DATA DEBUG ===");
      console.log("Raw data from API:", data[0]);
      
      // Parse carrierOptions - handle both old and new format
      let parsedCarrierOptions = [createDefaultOption()];
      
      const carrierOptionsField = data[0].carrierOptions || data[0].CarrierOptions;
      if (carrierOptionsField) {
        console.log("carrierOptions exists, raw value:", carrierOptionsField);
        try {
          const parsed = JSON.parse(carrierOptionsField);
          console.log("Parsed carrierOptions:", parsed);
          
          // Check if it's the new array format
          if (Array.isArray(parsed) && parsed.length > 0) {
            console.log("Using new array format");
            parsedCarrierOptions = parsed;
          }
          // Check if it's old object format (with carrier keys)
          else if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
            console.log("Converting from old object format");
            // Convert old format to new array format
            parsedCarrierOptions = Object.keys(parsed).map(carrierKey => {
              const option = parsed[carrierKey];
              return {
                carrier: carrierKey,
                incoterm: option.incoterm || '',
                currency: option.currency || '',
                cargoType: option.cargoType || '',
                freightCharges: option.freightCharges || [{ carrier: carrierKey, unitType: '', numberOfUnits: '', amount: '', currency: '', transitTime: '', numberOfRouting: '', surcharge: '', frequency: '', total: 0 }],
                destinationCharges: option.destinationCharges || [{ chargeName: '', unitType: '', numberOfUnits: '', amount: '', currency: '', total: 0 }],
                originHandling: option.originHandling || [{ chargeName: '', unitType: '', numberOfUnits: '', amount: '', currency: '', total: 0 }],
                destinationHandling: option.destinationHandling || [{ chargeName: '', unitType: '', numberOfUnits: '', amount: '', currency: '', total: 0 }]
              };
            });
            console.log("Converted to array:", parsedCarrierOptions);
          }
        } catch (e) {
          console.error("Error parsing carrierOptions:", e);
        }
      }
      // Handle very old format with separate carriers, freightCharges, etc.
      else if (data[0].carriers || data[0].Carriers) {
        console.log("Using very old format with separate fields");
        try {
          const carriersField = data[0].carriers || data[0].Carriers;
          const freightChargesField = data[0].freightCharges || data[0].FreightCharges;
          const destinationChargesField = data[0].destinationCharges || data[0].DestinationCharges;
          const originHandlingField = data[0].originHandling || data[0].OriginHandling;
          const destinationHandlingField = data[0].destinationHandling || data[0].DestinationHandling;
          
          const carriers = carriersField ? JSON.parse(carriersField) : [];
          const freightCharges = freightChargesField ? JSON.parse(freightChargesField) : [];
          const destinationCharges = destinationChargesField ? JSON.parse(destinationChargesField) : [];
          const originHandling = originHandlingField ? JSON.parse(originHandlingField) : [];
          const destinationHandling = destinationHandlingField ? JSON.parse(destinationHandlingField) : [];
          
          console.log("Old format carriers:", carriers);
          
          // Create options from old format
          if (carriers.length > 0) {
            parsedCarrierOptions = carriers.map((carrier, idx) => ({
              carrier: carrier.carrier || '',
              incoterm: carrier.incoterm || '',
              currency: carrier.currency || '',
              cargoType: carrier.cargoType || '',
              freightCharges: freightCharges.length > 0 ? freightCharges : [{ carrier: carrier.carrier || '', unitType: '', numberOfUnits: '', amount: '', currency: '', transitTime: '', numberOfRouting: '', surcharge: '', frequency: '', total: 0 }],
              destinationCharges: destinationCharges.length > 0 ? destinationCharges : [{ chargeName: '', unitType: '', numberOfUnits: '', amount: '', currency: '', total: 0 }],
              originHandling: originHandling.length > 0 ? originHandling : [{ chargeName: '', unitType: '', numberOfUnits: '', amount: '', currency: '', total: 0 }],
              destinationHandling: destinationHandling.length > 0 ? destinationHandling : [{ chargeName: '', unitType: '', numberOfUnits: '', amount: '', currency: '', total: 0 }]
            }));
            console.log("Converted old format to:", parsedCarrierOptions);
          }
        } catch (e) {
          console.error("Error converting old format:", e);
        }
      }

      const newFormData = {
        quoteId: data[0].quoteId || data[0].QuoteId || '',
        quoteNumber: data[0].quoteNumber || data[0].QuoteNumber || '',
        createdDate: formatDate(data[0].createdDate || data[0].CreatedDate),
        rateValidity: formatDate(data[0].rateValidity || data[0].RateValidity),
        customer: data[0].customer || data[0].Customer || '',
        contactName: data[0].contactName || data[0].ContactName || '',
        pickupLocation: data[0].pickupLocation || data[0].PickupLocation || '',
        deliveryLocation: data[0].deliveryLocation || data[0].DeliveryLocation || '',
        portOfLoading: data[0].portOfLoading || data[0].PortOfLoading || '',
        portOfDischarge: data[0].portOfDischarge || data[0].PortOfDischarge || '',
        equipment: (data[0].equipment || data[0].Equipment) ? JSON.parse(data[0].equipment || data[0].Equipment) : { equipment: '', units: '', netWeight: '', grossWeight: '', volume: '', chargeableWeight: '' },
        carrierOptions: parsedCarrierOptions,
        termsConditions: (data[0].termsConditions || data[0].TermsConditions) ? JSON.parse(data[0].termsConditions || data[0].TermsConditions) : [],
        createdAt: data[0].createdAt || data[0].CreatedAt || '',
        // Store user details from backend for PDF generation
        fullName: data[0].fullName || '',
        email: data[0].email || ''
      };
      
      console.log("Final form data to set:", newFormData);
      setFormData(newFormData);
    } catch (error) {
      console.error('Error loading quote:', error);
      toast.error('Failed to load quote data');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e, afterSave = 'none') => {
    if (e) e.preventDefault();

    // Validate customer
    if (!formData.customer || formData.customer.trim() === '') {
      toast.error('Customer is required. Please enter a customer name.');
      return;
    }

    const payload = {
      quoteId: formData.quoteId,
      quoteNumber: formData.quoteNumber,
      freightCategory: category,
      freightMode: mode,
      freightType: 'direct',
      createdDate: formData.createdDate,
      rateValidity: formData.rateValidity,
      customer: formData.customer,
      contactName: formData.contactName,
      pickupLocation: formData.pickupLocation,
      deliveryLocation: formData.deliveryLocation,
      portOfLoading: formData.portOfLoading,
      portOfDischarge: formData.portOfDischarge,
      equipment: JSON.stringify(formData.equipment),
      carrierOptions: JSON.stringify(formData.carrierOptions),
      termsConditions: JSON.stringify(formData.termsConditions),
      status: 'draft',
      createdBy: userDetails.emp_id,
      createdAt: quoteId ? (formData.createdAt || new Date().toISOString()) : new Date().toISOString()
    };

    try {
      if (quoteId) {
        console.log("Update payload:", payload);
        await updateQuote(payload);
        toast.success('Quote updated successfully!');
      } else {
        const { quoteId: _qId, ...createPayload } = payload;
        const result = await createNewQuote(createPayload);
        // Capture the new quoteId from the response
        if (result?.quoteId) {
          setFormData(prev => ({ ...prev, quoteId: result.quoteId }));
        }
        toast.success('Quote created successfully!');
      }

      if (afterSave === 'print') {
        await handlePrintPDF();
      } else if (afterSave === 'download') {
        await handleDownloadPDF();
      } else {
        navigate(-1);
      }
    } catch (error) {
      console.error('Error saving quote:', error);
      toast.error(error.response?.data?.message || 'Failed to save quote');
    }
  };

  const addCarrierOption = () => {
    if (disabled) return;
    setFormData(prev => ({
      ...prev,
      carrierOptions: [...prev.carrierOptions, createDefaultOption()]
    }));
  };

  const removeCarrierOption = (index) => {
    if (disabled || formData.carrierOptions.length <= 1) return;
    setFormData(prev => ({
      ...prev,
      carrierOptions: prev.carrierOptions.filter((_, i) => i !== index)
    }));
  };

  // Add freight charges table to a carrier option
  const addFreightChargesTable = (carrierIdx) => {
    if (disabled) return;

    const updated = [...formData.carrierOptions];
    const tables = updated[carrierIdx].freightChargesTables || [];
    const nextTableNum = tables.length + 1;

    const newTable = {
      tableName: `Option ${nextTableNum}`,
      charges: [{
        carrier: updated[carrierIdx].carrier || '',
        unitType: '',
        numberOfUnits: '',
        amount: '',
        currency: updated[carrierIdx].currency || '',
        transitTime: '',
        numberOfRouting: '',
        surcharge: '',
        frequency: '',
        total: 0,
        remarks: ''
      }]
    };

    // Initialize freightChargesTables if it doesn't exist
    if (!updated[carrierIdx].freightChargesTables) {
      updated[carrierIdx].freightChargesTables = [{
        tableName: 'Default',
        charges: updated[carrierIdx].freightCharges || []
      }];
    }

    updated[carrierIdx].freightChargesTables.push(newTable);
    setFormData(prev => ({ ...prev, carrierOptions: updated }));
  };

  // Remove freight charges table from a carrier option
  const removeFreightChargesTable = (carrierIdx, tableIdx) => {
    if (disabled || tableIdx === 0) return; // Can't remove first table

    const updated = [...formData.carrierOptions];
    if (updated[carrierIdx].freightChargesTables) {
      updated[carrierIdx].freightChargesTables = updated[carrierIdx].freightChargesTables.filter((_, i) => i !== tableIdx);
      setFormData(prev => ({ ...prev, carrierOptions: updated }));
    }
  };

  // Expose functions to window for CarrierOptionSection to use
  useEffect(() => {
    window.addFreightChargesTable = addFreightChargesTable;
    window.removeFreightChargesTable = removeFreightChargesTable;
    return () => {
      delete window.addFreightChargesTable;
      delete window.removeFreightChargesTable;
    };
  }, [formData, disabled]);

  const preparePDFData = async () => {
    let customerAddress = '';
    if (formData.customer) {
      try {
        customerAddress = await fetchAccountAddress(formData.customer);
        console.log("Customer Address:", customerAddress);
      } catch (error) {
        console.error("Error fetching customer address:", error);
      }
    }

    return {
      quoteNumber: formData.quoteNumber,
      freightCategory: category,
      freightMode: mode,
      freightType: 'direct',
      createdDate: formData.createdDate,
      rateValidity: formData.rateValidity,
      customer: formData.customer,
      contactName: formData.contactName,
      customerAddress: customerAddress,
      pickupLocation: formData.pickupLocation,
      deliveryLocation: formData.deliveryLocation,
      portOfLoading: formData.portOfLoading,
      portOfDischarge: formData.portOfDischarge,
      equipment: JSON.stringify(formData.equipment),
      carrierOptions: JSON.stringify(formData.carrierOptions),
      termsConditions: JSON.stringify(formData.termsConditions)
    };
  };

  const getUserDataForPDF = () => {
    // When viewing existing quote, use data from formData if available
    if (quoteId && formData.fullName) {
      // Split fullName into firstName and lastName
      const nameParts = formData.fullName.trim().split(' ');
      const firstName = nameParts[0] || '';
      const lastName = nameParts.slice(1).join(' ') || '';
      
      return {
        firstName: firstName,
        lastName: lastName,
        email: formData.email || ''
      };
    }
    
    // For new quotes or edit mode, use current user details
    if (userDetails) {
      return {
        firstName: userDetails.fname || '',
        lastName: userDetails.lname || '',
        email: userDetails.email || ''
      };
    }
    
    return null;
  };

  const handleDownloadPDF = async () => {
    const pdfData = await preparePDFData();
    const userData = getUserDataForPDF();
    generateDirectQuotePDF(pdfData, userData);
  };

  const handlePrintPDF = async () => {
    const pdfData = await preparePDFData();
    const userData = getUserDataForPDF();
    printDirectQuotePDF(pdfData, userData);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} onKeyDown={(e) => { if (e.key === 'Enter' && e.target.tagName !== 'TEXTAREA') e.preventDefault(); }} className="space-y-6">
      <div className="bg-white rounded-lg shadow p-6">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <div>
            <h2 className="text-xl font-semibold text-gray-800">
              {category?.toUpperCase()} - {mode?.toUpperCase()} (Direct)
            </h2>
            {isViewMode && (
              <span className="text-sm text-blue-600 font-medium">View Mode</span>
            )}
            {isEditMode && (
              <span className="text-sm text-green-600 font-medium">Edit Mode</span>
            )}
          </div>
          <div className="flex gap-2">
            {quoteId && (
              <>
                <button
                  type="button"
                  onClick={handleDownloadPDF}
                  className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                >
                  <FileDown size={18} />
                  Download PDF
                </button>
                <button
                  type="button"
                  onClick={handlePrintPDF}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  <Printer size={18} />
                  Print
                </button>
              </>
            )}
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="flex items-center gap-2 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
            >
              <ArrowLeft size={18} />
              Back
            </button>
          </div>
        </div>

        {/* Basic Information */}
        <BasicInfoSection formData={formData} setFormData={setFormData} disabled={disabled} />

        {/* Port Details */}
        <PortDetailsSection formData={formData} setFormData={setFormData} disabled={disabled} />

        {/* Equipment Details */}
        <EquipmentSection formData={formData} setFormData={setFormData} category={category} disabled={disabled} />

        {/* Carrier Options Section */}
        <div className="mb-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold text-gray-700">
              {category === 'air' ? 'Airline Options' : 'Sea Options'}
            </h3>
            {!disabled && (
              <button
                type="button"
                onClick={addCarrierOption}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 shadow-md transition-all hover:shadow-lg"
              >
                <Plus size={18} />
                Add Option
              </button>
            )}
          </div>

          {/* Render all carrier options */}
          {formData.carrierOptions.map((option, index) => (
            <CarrierOptionSection
              key={index}
              option={option}
              index={index}
              category={category}
              formData={formData}
              setFormData={setFormData}
              onRemove={() => removeCarrierOption(index)}
              disabled={disabled}
              totalOptions={formData.carrierOptions.length}
            />
          ))}
        </div>

        {/* Terms & Conditions */}
        <TermsConditionsSection formData={formData} setFormData={setFormData} disabled={disabled} />

        {/* Save Buttons */}
        {!disabled && (
          <div className="flex justify-end gap-2 mt-6 flex-wrap">
            <button
              type="submit"
              className="flex items-center gap-2 px-5 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 text-sm"
            >
              <Save size={16} />
              {quoteId ? 'Update' : 'Save'}
            </button>
            <button
              type="button"
              onClick={() => handleSubmit(null, 'print')}
              className="flex items-center gap-2 px-5 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
            >
              <Printer size={16} />
              {quoteId ? 'Update & Print' : 'Save & Print'}
            </button>
            <button
              type="button"
              onClick={() => handleSubmit(null, 'download')}
              className="flex items-center gap-2 px-5 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm"
            >
              <FileDown size={16} />
              {quoteId ? 'Update & Download' : 'Save & Download'}
            </button>
          </div>
        )}
      </div>
    </form>
  );
}